import type { CustomerLedgerEntry } from '@noconflict/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingSummary, useCustomerLedger } from '../../../entities/ledger';
import { useFormat } from '../../../shared/i18n/format';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/ui/card';
import { DataTable, type DataTableColumn } from '../../../shared/ui/data-table';
import { ErrorState } from '../../../shared/ui/error-state';
import { Skeleton } from '../../../shared/ui/skeleton';

/**
 * The organization's account: balance, how much of it is invoiced, and the history. Mounted only while amounts are
 * visible; the functions behind it answer nothing otherwise, and nothing at all for an org that never had an
 * account, in which case the card does not render.
 */
export function AccountBalanceCard({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation('org');
  const { formatCurrency, formatDate } = useFormat();
  const summary = useBillingSummary(organizationId);
  const history = useCustomerLedger(organizationId, summary.data != null);

  const describe = useCallback(
    (e: CustomerLedgerEntry): string => {
      const what =
        e.type === 'order_charge' && e.project_name
          ? e.order_reference
            ? t('settings.account.order', { project: e.project_name, reference: e.order_reference })
            : t('settings.account.orderNoReference', { project: e.project_name })
          : t(`settings.account.types.${e.type}`);
      return e.is_reversal ? t('settings.account.reversed', { what }) : what;
    },
    [t],
  );

  const columns = useMemo<DataTableColumn<CustomerLedgerEntry>[]>(
    () => [
      {
        id: 'date',
        header: t('settings.account.columns.date'),
        sortable: true,
        value: (e) => e.created_at,
        cell: (e) => <span className="whitespace-nowrap">{formatDate(e.created_at)}</span>,
      },
      {
        id: 'what',
        header: t('settings.account.columns.what'),
        value: (e) => describe(e),
        // the invoice goes under the entry, so the table keeps three columns on a phone
        cell: (e) => (
          <span className="grid">
            <span>{describe(e)}</span>
            {e.invoice_ref ? (
              <span className="text-sm text-muted-foreground">
                {t('settings.account.invoice', {
                  ref: e.invoice_ref,
                  status: t(`settings.account.invoiceStatus.${e.invoice_status ?? 'issued'}`),
                })}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'amount',
        header: t('settings.account.columns.amount'),
        align: 'right',
        value: (e) => e.amount_cents,
        cell: (e) => (
          <span className="whitespace-nowrap">{formatCurrency(e.amount_cents, e.currency)}</span>
        ),
      },
    ],
    [t, formatCurrency, formatDate, describe],
  );

  // Nothing until we know there is an account to show: most card organizations never have one.
  if (!summary.data && !summary.isError) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="font-sans">
          <h2>{t('settings.account.title')}</h2>
        </CardTitle>
        <CardDescription>{t('settings.account.help')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        {summary.isError ? (
          <ErrorState
            size="compact"
            title={t('settings.account.error')}
            description={summary.error.message}
            onRetry={() => void summary.refetch()}
          />
        ) : null}
        {summary.data ? (
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-muted-foreground">{t('settings.account.balance')}</dt>
              <dd className="font-display text-2xl text-heading">
                {formatCurrency(summary.data.balance_cents, summary.data.currency)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t('settings.account.invoiced')}</dt>
              <dd className="font-display text-2xl text-heading">
                {formatCurrency(summary.data.invoiced_unpaid_cents, summary.data.currency)}
              </dd>
            </div>
          </dl>
        ) : null}
        {history.isError ? (
          <ErrorState
            size="compact"
            title={t('settings.account.historyError')}
            description={history.error.message}
            onRetry={() => void history.refetch()}
          />
        ) : null}
        {history.isPending ? (
          <section
            aria-busy="true"
            aria-label={t('settings.account.loading')}
            className="grid gap-3"
          >
            <Skeleton className="h-24 w-full" />
          </section>
        ) : null}
        {history.data ? (
          <DataTable
            caption={t('settings.account.history')}
            columns={columns}
            data={history.data}
            getRowId={(e) => e.id}
            size="compact"
            empty={t('settings.account.empty')}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
